import client, { unwrap, unwrapPage } from './client.js';

/**
 * Every admin endpoint, in one place.
 *
 * Components call these rather than building URLs, so a route change is a
 * one-file edit and nothing constructs a query string by hand.
 */

const get = async (path, params) => unwrap(await client.get(path, { params }));
const getPage = async (path, params) => unwrapPage(await client.get(path, { params }));
const post = async (path, body) => unwrap(await client.post(path, body));
const patch = async (path, body) => unwrap(await client.patch(path, body));
const put = async (path, body) => unwrap(await client.put(path, body));
// DELETE carries a body here — the typed reason every audited removal needs.
const del = async (path, body) => unwrap(await client.delete(path, { data: body }));

export const api = {
  auth: {
    login: (credentials) => post('/auth/login', credentials),
    me: () => get('/auth/me'),
  },

  dashboard: () => get('/dashboard'),

  users: {
    list: (params) => getPage('/users', params),
    detail: (id) => get(`/users/${id}`),
    suspend: (id, reason) => post(`/users/${id}/suspend`, { reason }),
    reinstate: (id) => post(`/users/${id}/reinstate`),
    // Hidden from real users: for test accounts made on production.
    setVisibility: (id, hidden, reason) => post(`/users/${id}/visibility`, { hidden, reason }),
    // Soft: the row and its audit trail stay, the account stops working.
    remove: (id, reason) => del(`/users/${id}`, { reason }),
    // "Verify yourself": approve, or reject with the reason the person sees.
    decideIdentity: (id, body) => post(`/users/${id}/identity`, body),
  },

  broker: {
    brokers: (params) => getPage('/broker/brokers', params),
    // Broker accounts with no agency yet: signed in, never finished onboarding.
    unfinished: (params) => getPage('/users', { ...params, role: 'broker', brokerProfile: 'missing' }),
    broker: (id) => get(`/broker/brokers/${id}`),
    moderateBroker: (id, body) => post(`/broker/brokers/${id}/moderate`, body),
    // An independent broker's reference, ticked once an admin has called them.
    checkReference: (id, index, checked) => post(`/broker/brokers/${id}/references/${index}/check`, { checked }),
    removeBroker: (id, reason) => del(`/broker/brokers/${id}`, { reason }),
    /*
     * A verification document, filed under the agency. Two steps because the
     * bytes never pass through the API: ask for a slot, PUT them straight to
     * storage, then confirm.
     */
    documentSlot: (id, body) => post(`/broker/brokers/${id}/documents/signed-url`, body),
    documentComplete: (id, assetId) => post(`/broker/brokers/${id}/documents/complete`, { assetId }),
    properties: (params) => getPage('/broker/properties', params),
    property: (id) => get(`/broker/properties/${id}`),
    moderateProperty: (id, body) => post(`/broker/properties/${id}/moderate`, body),
    // Soft: leads, enquiries and deals still point at the listing.
    removeProperty: (id, reason) => del(`/broker/properties/${id}`, { reason }),
    leads: (params) => getPage('/broker/leads', params),
    requirements: (params) => getPage('/broker/requirements', params),
    enquiries: (params) => getPage('/broker/enquiries', params),
    deals: (params) => getPage('/broker/deals', params),
    // Soft, like the owner's own delete: hidden from them, kept for brokers who paid.
    removeRequirement: (id, reason) => del(`/broker/requirements/${id}`, { reason }),
    removeEnquiry: (id, reason) => del(`/broker/enquiries/${id}`, { reason }),
  },

  credits: {
    ledger: (params) => getPage('/credits/ledger', params),
    packages: () => get('/config/credit-packages'),
    upsertPackage: (body) => put('/config/credit-packages', body),
    removePackage: (packageId) => del(`/config/credit-packages/${packageId}`),

    coupons: (params) => getPage('/config/credit-coupons', params),
    createCoupon: (body) => post('/config/credit-coupons', body),
    updateCoupon: (couponId, body) => put(`/config/credit-coupons/${couponId}`, body),
    removeCoupon: (couponId) => del(`/config/credit-coupons/${couponId}`),
  },

  ads: {
    list: (params) => getPage('/ads', params),
  },

  agents: {
    list: (params) => getPage('/agents', params),
    create: (body) => post('/agents', body),
    nextEmployeeId: () => get('/agents/next-employee-id'),
    detail: (id) => get(`/agents/${id}`),
    update: (id, body) => patch(`/agents/${id}`, body),
    remove: (id, reason) => del(`/agents/${id}`, { reason }),
  },

  moderation: {
    reports: (params) => getPage('/moderation/reports', params),
    // With what was reported: the listing and its lister, or the conversation.
    report: (id) => get(`/moderation/reports/${id}`),
    // { action, note } to act, or { dismiss: true, note } to close without acting.
    resolveReport: (id, body) => post(`/moderation/reports/${id}/resolve`, body),
    issues: (params) => getPage('/moderation/issues', params),
  },

  finance: {
    payments: (params) => getPage('/finance/payments', params),
    // Returns { payment, credits: { owed, reversed, shortfall } } for a credit purchase.
    refund: (id, body) => post(`/finance/payments/${id}/refund`, body),
    credits: (params) => getPage('/finance/credits', params),
    adjustCredits: (body) => post('/finance/credits/adjust', body),
  },

  // The home-screen carousel. Images go straight to storage: slot, PUT, confirm, then add.
  support: {
    list: (params) => getPage('/support/queries', params),
    summary: () => get('/support/summary'),
    detail: (id) => get(`/support/queries/${id}`),
    reply: (id, body) => post(`/support/queries/${id}/reply`, body),
    setStatus: (id, status) => patch(`/support/queries/${id}`, { status }),
  },

  banners: {
    list: () => get('/home-banners'),
    slot: (body) => post('/home-banners/signed-url', body),
    complete: (assetId) => post('/home-banners/complete', { assetId }),
    create: (body) => post('/home-banners', body),
    update: (id, body) => patch(`/home-banners/${id}`, body),
    reorder: (ids) => put('/home-banners/order', { ids }),
    remove: (id) => del(`/home-banners/${id}`),
  },

  notifications: {
    audience: () => get('/notifications/audience'),
    broadcast: (body) => post('/notifications/broadcast', body),
    // What has been sent before, newest first.
    broadcasts: (params) => getPage('/notifications/broadcasts', params),
  },

  config: {
    get: () => get('/config'),
    update: (patchBody) => patch('/config', patchBody),
  },

  audit: {
    list: (params) => getPage('/audit-logs', params),
  },

  admins: {
    list: () => get('/admins'),
    create: (body) => post('/admins', body),
  },
};

export default api;
